import { memo, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Body } from "../../../components/main-body/body";
import "./content-overview.css";
import { ContentFlow } from "./sections/content-flow";
import { ContentSummary } from "./sections/content-summary";
import { ContentRecent } from "./sections/content-recent";
import { ContentWarnings } from "./sections/content-warnings";
import useAdminEvents from "../../../hooks/useAdminEvents";

import ApiContentOverview from "../../../api/content-overview";

import { ReactComponent as CategoriesIcon } from "../../../assets/content-overview-images/categories.svg";
import { ReactComponent as DailyTasksIcon } from "../../../assets/content-overview-images/dailyTasks.svg";
import { ReactComponent as ArticlesIcon } from "../../../assets/content-overview-images/articles.svg";
import { ReactComponent as FactsIcon } from "../../../assets/content-overview-images/facts.svg";
import { ReactComponent as LifePointsIcon } from "../../../assets/content-overview-images/lifePoints.svg";
import { ReactComponent as ScenariosIcon } from "../../../assets/content-overview-images/scenarios.svg";
import { ReactComponent as RulesIcon } from "../../../assets/content-overview-images/rules.svg";
import { ReactComponent as PlansIcon } from "../../../assets/content-overview-images/plans.svg";
// import { ReactComponent as TagsIcon } from "../../../assets/content-overview-images/tags.svg";

function buildSummaryConfig(projectCode) {
  return [
    {
      key: "categories",
      title: "Categories",
      description:
        "Define high-level domains (Nourishment, Mind, Body) that organize content and control Task assignment within Plans. Must be configured first as they influence all downstream modules.",
      icon: <CategoriesIcon />,
      href: `/${projectCode}/content/categories`,
      pendingHref: `/${projectCode}/content/categories?statuses=pending`,
    },
    {
      key: "dailyTasks",
      title: "Tasks",
      description:
        "Discrete activities assigned to study subjects through Plans. Can be restricted by Category and Cohort. Tasks are referenced in Scenarios and are prerequisites for Plan creation.",
      icon: <DailyTasksIcon />,
      href: `/${projectCode}/content/tasks`,
      pendingHref: `/${projectCode}/content/tasks?statuses=pending`,
    },
    {
      key: "articles",
      title: "Articles",
      description:
        "Primary educational content associated with Tasks or displayed in Scenarios. Organized by Categories and Tags to provide contextual information to subjects.",
      icon: <ArticlesIcon />,
      href: `/${projectCode}/content/articles`,
      pendingHref: `/${projectCode}/content/articles?statuses=pending`,
    },
    {
      key: "facts",
      title: "FitFacts",
      description:
        "Short, concise informational items delivered through Scenarios according to Plan schedules. Unlike Articles, FitFacts are only shown when Scenarios are triggered.",
      icon: <FactsIcon />,
      href: `/${projectCode}/content/fitfacts`,
      pendingHref: `/${projectCode}/content/fitfacts?statuses=pending`,
    },
    {
      key: "lifePoints",
      title: "LifePoints",
      description:
        "Configure the reward system by defining points awarded for completing Tasks by Category and for predefined events like completing Scenarios or connecting health devices.",
      icon: <LifePointsIcon />,
      href: `/${projectCode}/content/lifepoints`,
      pendingHref: null,
    },
    {
      key: "scenarios",
      title: "Scenarios",
      description:
        "Chatbot-driven communication flows with branching logic and decision paths. Visualized as communication trees and mapped to Plans to determine delivery timing.",
      icon: <ScenariosIcon />,
      href: `/${projectCode}/content/scenarios`,
      pendingHref: `/${projectCode}/content/scenarios?statuses=pending`,
    },
    {
      key: "rules",
      title: "Rules",
      description:
        "Define content-blocking logic based on subject profile attributes. Prevent specific Categories or tagged content from being shown to subjects who meet defined criteria.",
      icon: <RulesIcon />,
      href: `/${projectCode}/content/rules`,
      pendingHref: null,
    },
    {
      key: "plans",
      title: "Plans",
      description:
        "Define the study schedule and engagement routine. Orchestrate when Tasks and Scenarios occur by mapping them to specific days across multiple weeks for each cohort.",
      icon: <PlansIcon />,
      href: `/${projectCode}/content/plans`,
      pendingHref: `/${projectCode}/content/plans?statuses=pending`,
    },
    {
      key: "onboardingSurveys",
      title: "Onboarding",
      description:
        "Onboarding configuration using chatbot-driven communication flows with branching logic and decision paths. Visualized as communication trees and associated to Cohorts.",
      icon: <ScenariosIcon />,
      href: `/${projectCode}/content/onboardings`,
      pendingHref: `/${projectCode}/content/onboardings?statuses=pending`,
    },
    // {
    //   key: "tags",
    //   title: "Tags",
    //   description:
    //     "Flexible, free-form labels applied to Articles and FitFacts. Support additional filtering and content organization beyond structured Categories",
    //   icon: <TagsIcon />,
    //   href: `/${projectCode}/content/tags`,
    //   pendingHref: null,
    // },
  ];
}

function parseStatsResponse(data) {
  return data?.summary ?? data?.stats?.summary ?? null;
}

function getTimeAgo(lastUpdatedAt) {
  if (!lastUpdatedAt) return null;

  const t = new Date(lastUpdatedAt).getTime();
  if (Number.isNaN(t)) return null;

  const diffMs = Date.now() - t;

  if (diffMs < 60_000) {
    return { type: "time_ago", value: { unit: "minute", value: 0, text: "just now" } };
  }

  const min = Math.floor(diffMs / 60_000);
  if (min < 60) {
    return { type: "time_ago", value: { unit: "minute", value: min, text: `${min} min ago` } };
  }

  const hr = Math.floor(min / 60);
  if (hr < 48) {
    return { type: "time_ago", value: { unit: "hour", value: hr, text: `${hr} hours ago` } };
  }

  const days = Math.floor(hr / 24);
  return { type: "time_ago", value: { unit: "day", value: days, text: `${days} days ago` } };
}

async function fetchOverviewStats() {
  const resp = await ApiContentOverview.get();
  return resp.data;
}

const CONTENT_OVERVIEW_REFRESH_DELAY_MS = 500;

/** --- localStorage helpers --- */
const STORAGE_PREFIX = "contentOverview.blocks.v1";
const getStorageKey = (projectCode) => `${STORAGE_PREFIX}.${projectCode}`;

function safeReadJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function safeWriteJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export const ContentOverview = memo(() => {
  const { projectCode } = useParams();

  const [summaryStats, setSummaryStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [warningsData, setWarningsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);
  const mountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const loadOverviewStats = useCallback(async ({ showLoading = true } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      if (showLoading) setLoading(true);
      setError(null);

      const data = await fetchOverviewStats();
      const summary = parseStatsResponse(data);
      const recent = data?.stats?.recent ?? data?.recent ?? null;
      const warningsData = data?.stats?.warnings ?? data?.warnings ?? null;

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      setSummaryStats(summary);
      setRecent(recent);
      setWarningsData(warningsData);
    } catch (e) {
      console.error(e);
      if (mountedRef.current && requestId === requestIdRef.current) setError(e);
    } finally {
      if (showLoading && mountedRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const scheduleOverviewRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      loadOverviewStats({ showLoading: false });
    }, CONTENT_OVERVIEW_REFRESH_DELAY_MS);
  }, [loadOverviewStats]);

  // --- blocks open state (default true, but restored from localStorage) ---
  const [blocksOpen, setBlocksOpen] = useState(() => {
    const key = getStorageKey(projectCode);
    const saved = safeReadJSON(key);
    // default open for all blocks
    const base = { flow: true, recent: true, summary: true, warnings: true };
    return saved && typeof saved === "object" ? { ...base, ...saved } : base;
  });

  // if projectCode changes — reload saved state for that project
  useEffect(() => {
    const key = getStorageKey(projectCode);
    const saved = safeReadJSON(key);
    const base = { flow: true, recent: true, summary: true, warnings: true  };
    setBlocksOpen(saved && typeof saved === "object" ? { ...base, ...saved } : base);
  }, [projectCode]);

  const setBlockOpen = useCallback(
    (blockKey, isOpen) => {
      setBlocksOpen((prev) => {
        const next = { ...prev, [blockKey]: !!isOpen };
        safeWriteJSON(getStorageKey(projectCode), next);
        return next;
      });
    },
    [projectCode]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadOverviewStats();

    return () => {
      mountedRef.current = false;

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [projectCode, loadOverviewStats]);

  useAdminEvents({
    onCategoryChanged: scheduleOverviewRefresh,
    onTagChanged: scheduleOverviewRefresh,
    onArticleChanged: scheduleOverviewRefresh,
    onFitFactChanged: scheduleOverviewRefresh,
    onTaskChanged: scheduleOverviewRefresh,
    onScenarioChanged: scheduleOverviewRefresh,
    onOnboardingChanged: scheduleOverviewRefresh,
    onPlanChanged: scheduleOverviewRefresh,
    onRuleChanged: scheduleOverviewRefresh,
    onLifePointChanged: scheduleOverviewRefresh,
  });

  const summaryConfig = useMemo(() => buildSummaryConfig(projectCode), [projectCode]);

  const summaryItems = useMemo(() => {
    return summaryConfig.map((cfg) => {
      const s = summaryStats?.[cfg.key];

      const total = typeof s?.total === "number" ? s.total : null;
      const pending = typeof s?.pending === "number" ? s.pending : null;
      const lastUpdatedAt = s?.lastUpdatedAt ?? null;

      const meta = [];

      if (pending != null && pending > 0) {
        meta.push({
          type: "pending",
          value: pending,
          href: cfg.pendingHref || cfg.href,
        });
      }

      if (total === 0 && meta.length === 0) {
        meta.push({
          type: "empty",
          value: true,
          href: cfg.href,
        });
      }

      const timeAgo = getTimeAgo(lastUpdatedAt);
      if (timeAgo) meta.push(timeAgo);

      return {
        key: cfg.key,
        title: cfg.title,
        description: cfg.description,
        icon: cfg.icon,
        href: cfg.href,
        total,
        meta,
      };
    });
  }, [summaryConfig, summaryStats]);

  return (
    <Body header={{ title: "Overview" }} scroll>
      <div className="content-overview_page">
        <p className="content-overview_text">
          Configure and validate platform modules to ensure production readiness for study deployment
        </p>

        <div className="content-overview_blocks">
          <ContentWarnings
            warningsData={warningsData}
            loading={loading}
            error={error}
            defaultOpen={!!blocksOpen.warnings}
            onToggle={(open) => setBlockOpen("warnings", open)}
          />

          <ContentRecent
            recent={recent}
            loading={loading}
            error={error}
            defaultOpen={!!blocksOpen.recent}
            onToggle={(open) => setBlockOpen("recent", open)}
          />
          
          <ContentSummary
            items={summaryItems}
            loading={loading}
            error={error}
            defaultOpen={!!blocksOpen.summary}
            onToggle={(open) => setBlockOpen("summary", open)}
          />

          <ContentFlow
            defaultOpen={!!blocksOpen.flow}
            onToggle={(open) => setBlockOpen("flow", open)}
          />
        </div>
      </div>
    </Body>
  );
});
